/**
 * Feature Flags Configuration
 *
 * Controls which optional features are enabled in the application.
 * Features can be enabled/disabled via environment variables.
 * This allows deployment flexibility - not all services need to be running.
 *
 * IMPORTANT: Next.js only inlines NEXT_PUBLIC_* env vars when accessed with
 * literal strings (e.g., process.env.NEXT_PUBLIC_FEATURE_PHOENIX).
 * Dynamic access like process.env[variable] won't work at build time.
 */

// Helper to check if an env value is truthy
function isTruthy(value: string | undefined, defaultEnabled: boolean): boolean {
  if (value === undefined || value === '') {
    return defaultEnabled;
  }
  const normalized = value.toLowerCase().trim();
  return normalized === 'true' || normalized === '1' || normalized === 'enabled';
}

// Feature flag definitions with metadata
const FEATURE_FLAGS = {
  firecrawl: {
    envVar: 'NEXT_PUBLIC_FEATURE_FIRECRAWL',
    defaultEnabled: false,
    description: 'FireCrawl web scraping integration',
  },
  scaleTesting: {
    envVar: 'NEXT_PUBLIC_FEATURE_SCALE_TESTING',
    defaultEnabled: true,
    description: 'Scale testing and simulation',
  },
  a2ui: {
    envVar: 'NEXT_PUBLIC_FEATURE_A2UI',
    defaultEnabled: true,
    description: 'A2UI catalogs, multimodality settings, and catalog workspace',
  },
  agents: {
    envVar: 'NEXT_PUBLIC_FEATURE_AGENTS',
    defaultEnabled: true,
    description: 'Real-time agent authoring, dashboard, and agent testing (ai-orchestrator)',
  },
  workflows: {
    envVar: 'NEXT_PUBLIC_FEATURE_WORKFLOWS',
    defaultEnabled: true,
    description: 'Async workflow authoring and runtime (async-agent)',
  },
  costEstimates: {
    envVar: 'NEXT_PUBLIC_FEATURE_COST_ESTIMATES',
    defaultEnabled: true,
    description: 'Provider-cost forecasts, measured usage, and costing administration',
  },
  sip: {
    envVar: 'NEXT_PUBLIC_FEATURE_SIP',
    defaultEnabled: true,
    description: 'SIP telephony stack (Asterisk/Kamailio/rtpengine/Janus) settings and tools',
  },
  swimlanes: {
    envVar: 'NEXT_PUBLIC_FEATURE_SWIMLANES',
    defaultEnabled: false,
    description: 'Multi-swimlane pipeline authoring (parallel S2S agents, cross-feeds, turn arbitration)',
  },
  aiGeneration: {
    envVar: 'NEXT_PUBLIC_FEATURE_AI_GENERATION',
    defaultEnabled: false,
    description: 'Generate with AI: CLI-agent-powered agent/workflow generation (workspace-agent-runner)',
  },
  procedures: {
    envVar: 'NEXT_PUBLIC_FEATURE_PROCEDURES',
    defaultEnabled: true,
    description: 'Procedure supervision authoring (step-card editor + Preview checklist); the runtime engages purely on config presence',
  },
  teams: {
    envVar: 'NEXT_PUBLIC_FEATURE_TEAMS',
    defaultEnabled: false,
    description: 'Microsoft Teams agent exposure (settings panel + package download); hidden until ready to test',
  },
  avatar: {
    envVar: 'NEXT_PUBLIC_FEATURE_AVATAR',
    defaultEnabled: false,
    description: 'Avatar (Azure Avatar / Bey) settings + tester avatar view — rarely used, off by default',
  },
  realSmsTesting: {
    envVar: 'NEXT_PUBLIC_FEATURE_REAL_SMS_TESTING',
    defaultEnabled: false,
    description: 'SMS mode in full-screen agent Preview for sending test messages to the caller ANI',
  },
  platformAdmin: {
    envVar: 'NEXT_PUBLIC_FEATURE_PLATFORM_ADMIN',
    defaultEnabled: true,
    description: 'Platform Admin console (super-admin only): manage tenants, users, memberships/roles, and platform super-admins, with write-back to Authentik groups. Defaults ON — it replaces the always-on platform-admin tenant management; set false as a kill-switch.',
  },
} as const;

export type FeatureFlag = keyof typeof FEATURE_FLAGS;

/**
 * Check if a feature is enabled based on environment variable.
 * Uses literal string access for Next.js build-time inlining.
 */
export function isFeatureEnabled(feature: FeatureFlag): boolean {
  // Must use literal string access for Next.js to inline NEXT_PUBLIC_* vars
  switch (feature) {
    case 'firecrawl':
      return isTruthy(process.env.NEXT_PUBLIC_FEATURE_FIRECRAWL, false);
    case 'scaleTesting':
      return isTruthy(process.env.NEXT_PUBLIC_FEATURE_SCALE_TESTING, true);
    case 'a2ui':
      return isTruthy(process.env.NEXT_PUBLIC_FEATURE_A2UI, true);
    case 'agents':
      return isTruthy(process.env.NEXT_PUBLIC_FEATURE_AGENTS, true);
    case 'workflows':
      return isTruthy(process.env.NEXT_PUBLIC_FEATURE_WORKFLOWS, true);
    case 'costEstimates':
      return isTruthy(process.env.NEXT_PUBLIC_FEATURE_COST_ESTIMATES, true);
    case 'sip':
      return isTruthy(process.env.NEXT_PUBLIC_FEATURE_SIP, true);
    case 'swimlanes':
      return isTruthy(process.env.NEXT_PUBLIC_FEATURE_SWIMLANES, false);
    case 'aiGeneration':
      return isTruthy(process.env.NEXT_PUBLIC_FEATURE_AI_GENERATION, false);
    case 'procedures':
      return isTruthy(process.env.NEXT_PUBLIC_FEATURE_PROCEDURES, true);
    case 'teams':
      return isTruthy(process.env.NEXT_PUBLIC_FEATURE_TEAMS, false);
    case 'avatar':
      return isTruthy(process.env.NEXT_PUBLIC_FEATURE_AVATAR, false);
    case 'realSmsTesting':
      return isTruthy(process.env.NEXT_PUBLIC_FEATURE_REAL_SMS_TESTING, false);
    case 'platformAdmin':
      return isTruthy(process.env.NEXT_PUBLIC_FEATURE_PLATFORM_ADMIN, true);
    default:
      return false;
  }
}

/**
 * Server-side A2UI feature check. Accepts either the NEXT_PUBLIC_* var (baked
 * at build time) or a plain FEATURE_A2UI runtime override so API routes can be
 * toggled without rebuilding the image.
 */
export function isA2uiEnabledServer(): boolean {
  const runtime = process.env.FEATURE_A2UI;
  if (runtime !== undefined && runtime !== '') {
    return isTruthy(runtime, true);
  }
  return isTruthy(process.env.NEXT_PUBLIC_FEATURE_A2UI, true);
}

/**
 * Server-side capability checks for modular deployments. Each accepts a plain
 * FEATURE_* runtime override (so API routes / proxy can toggle without a
 * rebuild) and falls back to the NEXT_PUBLIC_* var baked at build time.
 */
export function isAgentsEnabledServer(): boolean {
  const runtime = process.env.FEATURE_AGENTS;
  if (runtime !== undefined && runtime !== '') {
    return isTruthy(runtime, true);
  }
  return isTruthy(process.env.NEXT_PUBLIC_FEATURE_AGENTS, true);
}

export function isWorkflowsEnabledServer(): boolean {
  const runtime = process.env.FEATURE_WORKFLOWS;
  if (runtime !== undefined && runtime !== '') {
    return isTruthy(runtime, true);
  }
  return isTruthy(process.env.NEXT_PUBLIC_FEATURE_WORKFLOWS, true);
}

export function isCostEstimatesEnabledServer(): boolean {
  const runtime = process.env.FEATURE_COST_ESTIMATES;
  if (runtime !== undefined && runtime !== '') {
    return isTruthy(runtime, true);
  }
  return isTruthy(process.env.NEXT_PUBLIC_FEATURE_COST_ESTIMATES, true);
}

export function isSipEnabledServer(): boolean {
  const runtime = process.env.FEATURE_SIP;
  if (runtime !== undefined && runtime !== '') {
    return isTruthy(runtime, true);
  }
  return isTruthy(process.env.NEXT_PUBLIC_FEATURE_SIP, true);
}

export function isAiGenerationEnabledServer(): boolean {
  const runtime = process.env.FEATURE_AI_GENERATION;
  if (runtime !== undefined && runtime !== '') {
    return isTruthy(runtime, false);
  }
  return isTruthy(process.env.NEXT_PUBLIC_FEATURE_AI_GENERATION, false);
}

export function isTeamsEnabledServer(): boolean {
  const runtime = process.env.FEATURE_TEAMS;
  if (runtime !== undefined && runtime !== '') {
    return isTruthy(runtime, false);
  }
  return isTruthy(process.env.NEXT_PUBLIC_FEATURE_TEAMS, false);
}

export function isPlatformAdminEnabledServer(): boolean {
  const runtime = process.env.FEATURE_PLATFORM_ADMIN;
  if (runtime !== undefined && runtime !== '') {
    return isTruthy(runtime, true);
  }
  return isTruthy(process.env.NEXT_PUBLIC_FEATURE_PLATFORM_ADMIN, true);
}

export function isStrandsExportEnabledServer(): boolean {
  const runtime = process.env.ENABLE_STRANDS_EXPORT;
  if (runtime !== undefined && runtime !== '') {
    return isTruthy(runtime, false);
  }
  return isTruthy(process.env.NEXT_PUBLIC_ENABLE_STRANDS_EXPORT, false);
}

export function isCodeExportEnabledServer(): boolean {
  const runtime = process.env.ENABLE_CODE_EXPORT;
  if (runtime !== undefined && runtime !== '') {
    return isTruthy(runtime, false);
  }
  return isTruthy(process.env.NEXT_PUBLIC_ENABLE_CODE_EXPORT, false);
}

/**
 * Get all enabled features as an array.
 */
export function getEnabledFeatures(): FeatureFlag[] {
  return (Object.keys(FEATURE_FLAGS) as FeatureFlag[]).filter(isFeatureEnabled);
}

/**
 * Get feature configuration info (for debugging/admin UI).
 */
export function getFeatureInfo(feature: FeatureFlag): {
  enabled: boolean;
  description: string;
  envVar: string;
} {
  const config = FEATURE_FLAGS[feature];
  return {
    enabled: isFeatureEnabled(feature),
    description: config.description,
    envVar: config.envVar,
  };
}

/**
 * Get all feature configurations (for debugging/admin UI).
 */
export function getAllFeatureInfo(): Record<
  FeatureFlag,
  { enabled: boolean; description: string; envVar: string }
> {
  const result = {} as Record<
    FeatureFlag,
    { enabled: boolean; description: string; envVar: string }
  >;

  for (const feature of Object.keys(FEATURE_FLAGS) as FeatureFlag[]) {
    result[feature] = getFeatureInfo(feature);
  }

  return result;
}

// Map sections to their required features (if any)
// Performance/Knowledge tabs are now managed via admin settings (sidebar_custom_tabs)
export const SECTION_FEATURES: Partial<Record<string, FeatureFlag>> = {};

/**
 * Check if a section should be visible based on feature flags.
 * Sections without feature flag requirements are always visible.
 */
export function isSectionFeatureEnabled(section: string): boolean {
  const requiredFeature = SECTION_FEATURES[section];
  if (!requiredFeature) {
    return true; // No feature flag required
  }
  return isFeatureEnabled(requiredFeature);
}
